import type { VectorHandleMode, VectorNetwork } from "@/document/model/vectorNetwork";

export interface VectorHandleRef {
  edgeId: string;
  endpoint: "from" | "to";
}

export interface VectorHandleUpdate extends VectorHandleRef {
  x: number;
  y: number;
}

export function vectorHandleUpdatesForModeChange(
  network: VectorNetwork,
  vertexId: string,
  mode: VectorHandleMode,
  anchor?: VectorHandleRef,
): VectorHandleUpdate[] {
  if (mode === "none") {
    return [];
  }

  const vertex = network.vertices.find(candidate => candidate.id === vertexId);
  if (!vertex) {
    return [];
  }

  if (anchor) {
    const edge = network.edges.find(candidate => candidate.id === anchor.edgeId);
    const position = edge?.[anchor.endpoint].handle;
    if (!position) {
      return [];
    }

    return vectorHandleUpdatesForMove(network, anchor, position, mode);
  }

  const handles = handlesAtVertex(network, vertexId);
  if (handles.length === 0) {
    return [];
  }

  if (handles.length === 1) {
    const handle = handles[0]!;
    return vectorHandleUpdatesForMove(network, handle, { x: handle.x, y: handle.y }, mode);
  }

  if (handles.length === 2) {
    return alignVertexHandles(vertex, handles[0]!, handles[1]!, mode);
  }

  return handles.flatMap(handle => vectorHandleUpdatesForMove(network, handle, { x: handle.x, y: handle.y }, mode));
}

export function handlesAtVertex(
  network: VectorNetwork,
  vertexId: string,
): Array<VectorHandleRef & { x: number; y: number }> {
  const handles: Array<VectorHandleRef & { x: number; y: number }> = [];

  for (const edge of network.edges) {
    if (edge.from.vertexId === vertexId && edge.from.handle) {
      handles.push({
        edgeId: edge.id,
        endpoint: "from",
        x: edge.from.handle.x,
        y: edge.from.handle.y,
      });
    }

    if (edge.to.vertexId === vertexId && edge.to.handle) {
      handles.push({
        edgeId: edge.id,
        endpoint: "to",
        x: edge.to.handle.x,
        y: edge.to.handle.y,
      });
    }
  }

  return handles;
}

export function vectorHandleUpdatesForMove(
  network: VectorNetwork,
  handle: VectorHandleRef,
  position: { x: number; y: number },
  modeOverride?: Exclude<VectorHandleMode, "none">,
): VectorHandleUpdate[] {
  const edge = network.edges.find(candidate => candidate.id === handle.edgeId);
  if (!edge) {
    return [{ ...handle, ...position }];
  }

  const vertex = network.vertices.find(candidate => candidate.id === edge[handle.endpoint].vertexId);
  const mode = modeOverride ?? vertex?.handleMode ?? "none";
  if (!vertex || mode === "none") {
    return [{ ...handle, ...position }];
  }

  const opposite = findOppositeHandle(network, handle, edge[handle.endpoint].vertexId);
  if (!opposite) {
    return [{ ...handle, ...position }];
  }

  const dx = position.x - vertex.x;
  const dy = position.y - vertex.y;
  const movedLength = Math.hypot(dx, dy);
  if (movedLength <= 0.0001) {
    return [{ ...handle, ...position }];
  }

  if (!opposite.current) {
    return [
      { ...handle, ...position },
      {
        edgeId: opposite.edgeId,
        endpoint: opposite.endpoint,
        x: vertex.x - (dx / movedLength) * movedLength,
        y: vertex.y - (dy / movedLength) * movedLength,
      },
    ];
  }

  const length = mode === "mirrorAngleAndLength"
    ? movedLength
    : Math.hypot(opposite.current.x - vertex.x, opposite.current.y - vertex.y);

  return [
    { ...handle, ...position },
    {
      edgeId: opposite.edgeId,
      endpoint: opposite.endpoint,
      x: vertex.x - (dx / movedLength) * length,
      y: vertex.y - (dy / movedLength) * length,
    },
  ];
}

function findOppositeHandle(
  network: VectorNetwork,
  handle: VectorHandleRef,
  vertexId: string,
): (VectorHandleRef & { current?: { x: number; y: number } }) | undefined {
  for (const edge of network.edges) {
    if (!(edge.id === handle.edgeId && handle.endpoint === "from") && edge.from.vertexId === vertexId) {
      return { edgeId: edge.id, endpoint: "from", current: edge.from.handle };
    }

    if (!(edge.id === handle.edgeId && handle.endpoint === "to") && edge.to.vertexId === vertexId) {
      return { edgeId: edge.id, endpoint: "to", current: edge.to.handle };
    }
  }

  return undefined;
}

function alignVertexHandles(
  vertex: { x: number; y: number },
  first: VectorHandleRef & { x: number; y: number },
  second: VectorHandleRef & { x: number; y: number },
  mode: VectorHandleMode,
): VectorHandleUpdate[] {
  const firstVector = { x: first.x - vertex.x, y: first.y - vertex.y };
  const secondVector = { x: second.x - vertex.x, y: second.y - vertex.y };
  const firstLength = Math.hypot(firstVector.x, firstVector.y);
  const secondLength = Math.hypot(secondVector.x, secondVector.y);
  const axis = mirrorAxisForVectors(firstVector, secondVector);
  if (!axis) {
    return [];
  }

  const mirroredLength = mode === "mirrorAngleAndLength"
    ? (firstLength + secondLength) / 2
    : undefined;

  return [
    {
      edgeId: first.edgeId,
      endpoint: first.endpoint,
      x: vertex.x + axis.x * (mirroredLength ?? firstLength),
      y: vertex.y + axis.y * (mirroredLength ?? firstLength),
    },
    {
      edgeId: second.edgeId,
      endpoint: second.endpoint,
      x: vertex.x - axis.x * (mirroredLength ?? secondLength),
      y: vertex.y - axis.y * (mirroredLength ?? secondLength),
    },
  ];
}

function mirrorAxisForVectors(
  first: { x: number; y: number },
  second: { x: number; y: number },
): { x: number; y: number } | undefined {
  const firstLength = Math.hypot(first.x, first.y);
  const secondLength = Math.hypot(second.x, second.y);

  if (firstLength > 0.0001 && secondLength > 0.0001) {
    const axisX = first.x / firstLength - second.x / secondLength;
    const axisY = first.y / firstLength - second.y / secondLength;
    const axisLength = Math.hypot(axisX, axisY);
    if (axisLength > 0.0001) {
      return { x: axisX / axisLength, y: axisY / axisLength };
    }

    return { x: first.x / firstLength, y: first.y / firstLength };
  }

  if (firstLength > 0.0001) {
    return { x: first.x / firstLength, y: first.y / firstLength };
  }

  if (secondLength > 0.0001) {
    return { x: second.x / secondLength, y: second.y / secondLength };
  }

  return undefined;
}
